import { Suspense, useMemo, useRef } from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import { Environment, Float, Lightformer, MeshTransmissionMaterial, Sparkles as DreiSparkles } from "@react-three/drei";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const reducedMotion =
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Backdrop = () => {
  const texture = useMemo(() => {
    const c = document.createElement("canvas");
    c.width = c.height = 512;
    const ctx = c.getContext("2d");
    const g = ctx.createRadialGradient(320, 230, 18, 320, 235, 250);
    g.addColorStop(0, "#b8a77e");
    g.addColorStop(0.25, "#453a25");
    g.addColorStop(0.55, "#16130d");
    g.addColorStop(1, "#0E0E10");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 512, 512);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }, []);
  return (
    <mesh position={[0, 0, -4]}>
      <planeGeometry args={[26, 15]} />
      <meshBasicMaterial map={texture} toneMapped={false} />
    </mesh>
  );
};

const Diamond = () => {
  const ref = useRef();
  const offsetX = typeof window !== "undefined" && window.innerWidth >= 1024 ? 1.25 : 0;
  const geometry = useMemo(() => {
    const crown = new THREE.CylinderGeometry(0.62, 1, 0.35, 8, 1, true);
    crown.translate(0, 0.175, 0);
    const table = new THREE.CircleGeometry(0.62, 8);
    table.rotateX(-Math.PI / 2);
    table.translate(0, 0.35, 0);
    const pavilion = new THREE.ConeGeometry(1, 1.35, 8, 1, true);
    pavilion.rotateX(Math.PI);
    pavilion.translate(0, -0.675, 0);
    const g = mergeGeometries([crown, table, pavilion]);
    g.computeVertexNormals();
    return g;
  }, []);

  useFrame((state, delta) => {
    if (!ref.current) return;
    if (!reducedMotion) ref.current.rotation.y += delta * 0.06;
    const { x, y } = state.pointer;
    ref.current.rotation.x += (y * 0.1 - ref.current.rotation.x) * 0.015;
    ref.current.rotation.z += (x * 0.05 - ref.current.rotation.z) * 0.015;
  });

  return (
    <mesh ref={ref} geometry={geometry} scale={0.95} position={[offsetX, 0.05, 0]} data-testid="hero-diamond-mesh">
      <MeshTransmissionMaterial
        backside
        backsideThickness={0.4}
        backsideResolution={256}
        samples={6}
        resolution={512}
        transmission={1}
        thickness={0.6}
        roughness={0.02}
        ior={2.417}
        chromaticAberration={0.4}
        anisotropy={0.2}
        distortion={0}
        distortionScale={0}
        temporalDistortion={0}
        clearcoat={1}
        clearcoatRoughness={0}
        attenuationColor="#ffffff"
        attenuationDistance={3}
        color="#ffffff"
        envMapIntensity={1.2}
        flatShading
      />
    </mesh>
  );
};

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const DiamondHero3D = ({ onRequestInventory }) => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={sectionRef} data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
      <div className="absolute inset-0" data-testid="hero-3d-canvas">
        <Canvas camera={{ position: [0, 0.5, 4.6], fov: 40 }} dpr={[1, 1.8]} gl={{ antialias: true, alpha: true }}>
          <Suspense fallback={null}>
            <Backdrop />
            <ambientLight intensity={0.25} />
            <spotLight position={[4, 6, 4]} intensity={15} angle={0.45} color="#ffffff" />
            <Float speed={0.6} rotationIntensity={0.1} floatIntensity={0.3}>
              <Diamond />
            </Float>
            <DreiSparkles count={50} scale={[9, 5, 4]} size={1.3} speed={0.12} color="#E8DCC0" opacity={0.35} />
            <Environment resolution={256}>
              <group rotation={[-Math.PI / 3, 0, 1]}>
                <Lightformer form="circle" intensity={3.5} position={[0, 5, -9]} scale={3} />
                <Lightformer intensity={2} position={[-5, 1, -1]} scale={[6, 1.5]} />
                <Lightformer intensity={1.8} position={[10, 1, 0]} scale={[10, 1.5]} />
                <Lightformer intensity={2.5} position={[0, 6, 3]} scale={[12, 1]} />
                <Lightformer color="#C9A24B" intensity={1.2} position={[0, -5, 2]} scale={[7, 2]} />
                <Lightformer form="circle" intensity={1.5} position={[-3, -2, 5]} scale={1.2} />
              </group>
            </Environment>
          </Suspense>
        </Canvas>
      </div>

      <div className="hero-bloom pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0E0E10] via-[#0E0E10]/55 to-transparent lg:via-[#0E0E10]/35" />

      <motion.div
        style={{ y: textY, opacity: textOpacity }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-40 lg:px-12"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="eyebrow mb-6"
          data-testid="hero-eyebrow"
        >
          Certified Loose Diamonds · JLT, Dubai
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display max-w-4xl text-4xl font-light leading-[1.06] tracking-tight text-white sm:text-6xl lg:text-7xl"
        >
          <MaskedLine delay={0.35}>Rare stones.</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="gold-text italic">Traded with</span>
          </MaskedLine>
          <MaskedLine delay={0.65}>Dubai precision.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-[#B8BCC2] sm:text-lg"
          data-testid="hero-subcopy"
        >
          Sunrays sources certified natural and lab-grown loose diamonds for jewelers, retailers, and
          wholesale buyers — from 0.30ct melee to 15-carat statements, GIA, IGI, and HRD certified.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button data-testid="hero-request-inventory-btn" onClick={onRequestInventory} className="gold-btn">
            Request Inventory <ArrowRight className="h-4 w-4" />
          </button>
          <button data-testid="hero-explore-market-btn" onClick={() => navigate("/explorer")} className="glass-btn">
            Explore the Market
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-14 flex flex-wrap gap-x-10 gap-y-3 text-xs uppercase tracking-[0.25em] text-[#717680]"
          data-testid="hero-stats"
        >
          <span>0.30ct — 15.00ct+</span>
          <span>GIA · IGI · HRD</span>
          <span>Conflict-free sourcing</span>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => window.scrollBy({ top: window.innerHeight * 0.85, behavior: "smooth" })}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden text-[#717680] transition-colors hover:text-[#C9A24B] lg:block"
        aria-label="Scroll down"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.button>
    </section>
  );
};
